import type {
  InventoryItemRecord,
  InventoryRecipeRecord,
  SaveRecipeItemRequest,
  SaveRecipeRequest,
} from '@/renderer/types/inventory';

export interface RecipeLineFormState {
  tempId: string;
  inventoryItemId: string;
  inventoryItemLabel: string;
  quantity: string;
  unitId: string;
  unitLabel: string;
  notes: string;
}

export interface RecipeFormState {
  menuItemId: string;
  menuItemLabel: string;
  name: string;
  description: string;
  yieldQuantity: string;
  isActive: boolean;
  outletId: string;
  lines: RecipeLineFormState[];
}

export function createEmptyRecipeLine(): RecipeLineFormState {
  return {
    tempId: crypto.randomUUID(),
    inventoryItemId: '',
    inventoryItemLabel: '',
    quantity: '',
    unitId: '',
    unitLabel: '',
    notes: '',
  };
}

export function createEmptyRecipeForm(): RecipeFormState {
  return {
    menuItemId: '',
    menuItemLabel: '',
    name: '',
    description: '',
    yieldQuantity: '1',
    isActive: true,
    outletId: '',
    lines: [createEmptyRecipeLine()],
  };
}

export function createRecipeFormFromRecord(
  recipe: InventoryRecipeRecord,
): RecipeFormState {
  return {
    menuItemId: recipe.menu_item_id,
    menuItemLabel: recipe.menu_item_name ?? '',
    name: recipe.name,
    description: recipe.description ?? '',
    yieldQuantity: String(recipe.yield_quantity),
    isActive: recipe.is_active,
    outletId: recipe.outlet_id ?? '',
    lines: (recipe.items ?? []).map((item) => ({
      tempId: item.id,
      inventoryItemId: item.inventory_item_id,
      inventoryItemLabel: item.inventory_item_name ?? '',
      quantity: String(item.quantity),
      unitId: item.unit_id ?? '',
      unitLabel: '',
      notes: item.notes ?? '',
    })),
  };
}

export function applyItemToLine(
  line: RecipeLineFormState,
  item: InventoryItemRecord,
): RecipeLineFormState {
  return {
    ...line,
    inventoryItemId: item.id,
    inventoryItemLabel: item.name,
    unitId: item.stock_unit_id,
    unitLabel: '',
  };
}

export function buildSaveRecipePayload(
  companyId: number,
  form: RecipeFormState,
  existingId?: string,
): SaveRecipeRequest {
  const items: SaveRecipeItemRequest[] = form.lines
    .filter((l) => l.inventoryItemId && l.quantity)
    .map((l) => ({
      inventoryItemId: l.inventoryItemId,
      quantity: parseFloat(l.quantity) || 0,
      unitId: l.unitId || null,
      notes: l.notes || null,
    }));

  return {
    id: existingId,
    companyId,
    outletId: form.outletId || null,
    menuItemId: form.menuItemId,
    name: form.name,
    description: form.description || null,
    yieldQuantity: parseFloat(form.yieldQuantity) || 1,
    isActive: form.isActive,
    items,
  };
}
